import EventEmitter from '../Utils/EventEmitter.js'
import { BOT_ORDER, BUILD_SITES, DEPOSITS, getBotOrderRecipe, getMachinePort, MACHINE_TYPES, RESOURCE_ORDER, STORAGE_BIN, TRAY_SIZES, TUNING } from './definitions.js'
import Logistics from './Logistics.js'
import BotSystem from './BotSystem.js'

const distance = (_a, _b) => Math.hypot(_a.x - _b.x, _a.y - _b.y)
const insideTray = (_point, _tray, _size) =>
    Math.abs(_point.x - _tray.x) <= _size.width / 2 &&
    Math.abs(_point.y - _tray.y) <= _size.depth / 2

const OUTPUT_TRAY_BELTS = {
    ironFabricator: 'ironSplitBelt',
    copperFabricator: 'copperSplitBelt',
    circuitFabricator: 'siliconBarNetworkBelt'
}

export default class GameState extends EventEmitter
{
    constructor(_save = null)
    {
        super()
        this.elapsed = 0
        this.player = { x: 0, y: 8, stationaryTime: 0 }
        this.inventory = Object.fromEntries(RESOURCE_ORDER.map((_resource) => [_resource, 0]))
        this.deposits = DEPOSITS.map((_deposit) => ({
            ..._deposit,
            remaining: _deposit.amount,
            occupied: false,
            revealed: !['ironOre', 'copperOre', 'coal', 'siliconOre'].includes(_deposit.resource),
            regrowAt: null
        }))
        this.sites = BUILD_SITES.map((_site, _index) => ({
            ..._site,
            deposited: Object.fromEntries(Object.keys(_site.recipe).map((_resource) => [_resource, 0])),
            built: false,
            revealed: _index === 0
        }))
        this.activeSiteIndex = 0
        this.machines = []
        this.logistics = new Logistics(this)
        this.botSystem = new BotSystem(this)
        this.botOrder = {
            ...BOT_ORDER,
            revealed: false,
            recipe: getBotOrderRecipe(0),
            deposited: Object.fromEntries(Object.keys(BOT_ORDER.baseRecipe).map((_resource) => [_resource, 0]))
        }
        this.bin = {
            ...STORAGE_BIN,
            revealed: false,
            stored: Object.fromEntries(RESOURCE_ORDER.map((_resource) => [_resource, 0]))
        }
        this.gatherCooldown = 0
        this.transferCooldown = 0
        this.action = null
        this.restore(_save)
    }

    restore(_save)
    {
        if(!_save)
            return
        this.elapsed = _save.elapsed ?? this.elapsed
        this.player.x = _save.player?.x ?? this.player.x
        this.player.y = _save.player?.y ?? this.player.y
        this.inventory = { ...this.inventory, ..._save.inventory }
        for(const deposit of this.deposits)
            Object.assign(deposit, _save.deposits?.[deposit.id] ?? {})
        for(const site of this.sites)
        {
            const saved = _save.sites?.[site.id]
            if(!saved)
                continue
            site.deposited = { ...site.deposited, ...saved.deposited }
            site.built = saved.built ?? site.built
            site.revealed = saved.revealed ?? site.revealed
        }
        this.activeSiteIndex = _save.activeSiteIndex ?? this.activeSiteIndex
        if(Array.isArray(_save.machines))
            this.machines = _save.machines
                .filter((_machine) => MACHINE_TYPES[_machine.type])
                .map((_machine) =>
                {
                    const site = this.sites.find((_site) => _site.id === _machine.id)
                    return {
                        ..._machine,
                        x: site?.machineX ?? site?.x ?? _machine.x,
                        y: site?.machineY ?? site?.y ?? _machine.y,
                        receivedInputs: new Set(_machine.receivedInputs)
                    }
                })
        this.bin.revealed = _save.bin?.revealed ?? this.bin.revealed
        this.bin.stored = { ...this.bin.stored, ..._save.bin?.stored }
        this.gatherCooldown = _save.gatherCooldown ?? this.gatherCooldown
        this.transferCooldown = _save.transferCooldown ?? this.transferCooldown
        this.logistics.restore(_save.logistics)
        this.botSystem.restore(_save.bots)
        this.botOrder.revealed = _save.botOrder?.revealed ?? this.machines.some((_machine) => _machine.type === 'botFabricator')
        this.botOrder.deposited = { ...this.botOrder.deposited, ..._save.botOrder?.deposited }
        this.botOrder.recipe = getBotOrderRecipe(this.botSystem.bots.length)
        const previousSite = this.sites[this.activeSiteIndex - 1]
        if(this.activeSite && previousSite?.built && !this.activeSite.revealAfterProduction)
            this.activeSite.revealed = true
    }

    toJSON()
    {
        return {
            elapsed: this.elapsed,
            player: { x: this.player.x, y: this.player.y },
            inventory: this.inventory,
            deposits: Object.fromEntries(this.deposits.map((_deposit) => [_deposit.id, {
                remaining: _deposit.remaining,
                occupied: _deposit.occupied,
                revealed: _deposit.revealed,
                regrowAt: _deposit.regrowAt
            }])),
            sites: Object.fromEntries(this.sites.map((_site) => [_site.id, {
                deposited: _site.deposited,
                built: _site.built,
                revealed: _site.revealed
            }])),
            activeSiteIndex: this.activeSiteIndex,
            machines: this.machines.map((_machine) => ({
                ..._machine,
                receivedInputs: [..._machine.receivedInputs]
            })),
            bin: { revealed: this.bin.revealed, stored: this.bin.stored },
            logistics: this.logistics.toJSON(),
            bots: this.botSystem.toJSON(),
            botOrder: { revealed: this.botOrder.revealed, deposited: this.botOrder.deposited },
            gatherCooldown: this.gatherCooldown,
            transferCooldown: this.transferCooldown
        }
    }

    get activeSite()
    {
        return this.sites[this.activeSiteIndex] ?? null
    }

    get inventoryTotal()
    {
        return RESOURCE_ORDER.reduce((_total, _resource) => _total + this.inventory[_resource], 0)
    }

    get machineCount()
    {
        return this.machines.length
    }

    setPlayerPosition(_x, _y)
    {
        this.player.x = _x
        this.player.y = _y
    }

    update(_delta, _moving)
    {
        this.elapsed += _delta
        this.gatherCooldown = Math.max(0, this.gatherCooldown - _delta)
        this.transferCooldown = Math.max(0, this.transferCooldown - _delta)
        this.player.stationaryTime = _moving ? 0 : this.player.stationaryTime + _delta
        this.action = null

        this.updateTrees()
        for(const machine of this.machines)
        {
            if(machine.inputBlockedUntilExit && distance(this.player, machine) > TUNING.machineRange)
                machine.inputBlockedUntilExit = false
        }
        this.logistics.update(_delta)
        for(const machine of this.machines)
            this.updateMachine(machine, _delta)
        this.botSystem.update(_delta)

        if(this.player.stationaryTime < TUNING.stationaryDelay)
            return

        if(this.tryConstruction())
            return
        if(this.tryBinInput())
            return
        if(this.tryMachineInput())
            return
        if(this.tryGroundOutput())
            return
        if(this.tryMachineOutput())
            return
        this.tryGathering()
    }

    updateTrees()
    {
        for(const deposit of this.deposits)
        {
            if(deposit.tree && deposit.remaining === 0 && deposit.regrowAt !== null && this.elapsed >= deposit.regrowAt)
            {
                deposit.remaining = deposit.amount
                deposit.regrowAt = null
                this.trigger('depositChanged', [deposit])
            }
        }
    }

    updateMachine(_machine, _delta)
    {
        const definition = MACHINE_TYPES[_machine.type]
        const wasActive = _machine.active
        _machine.active = false

        if(!definition.output)
        {
            this.emitMachineActivity(_machine, wasActive)
            return
        }

        if(_machine.output >= TUNING.outputCapacity)
        {
            this.emitMachineActivity(_machine, wasActive)
            return
        }

        const deposit = definition.depositId ? this.deposits.find((_item) => _item.id === definition.depositId) : null
        if(deposit && deposit.remaining <= 0)
        {
            this.emitMachineActivity(_machine, wasActive)
            return
        }

        if(definition.fuel && _machine.fuelRemaining <= 0 && !this.consumeFuel(_machine))
        {
            this.emitMachineActivity(_machine, wasActive)
            return
        }

        if(_machine.progress === 0 && definition.inputs?.length)
        {
            if(definition.inputs.some((_resource) => (_machine.inputs[_resource] ?? 0) <= 0))
            {
                this.emitMachineActivity(_machine, wasActive)
                return
            }
            for(const resource of definition.inputs)
                _machine.inputs[resource]--
            this.trigger('machineChanged', [_machine])
        }

        _machine.active = true
        const step = definition.fuel ? Math.min(_delta, _machine.fuelRemaining) : _delta
        _machine.progress += step
        if(definition.fuel)
            _machine.fuelRemaining = Math.max(0, _machine.fuelRemaining - step)

        if(_machine.progress + 0.0001 >= definition.duration)
        {
            _machine.progress = 0
            _machine.output++
            if(deposit)
            {
                deposit.remaining--
                this.trigger('depositChanged', [deposit])
            }
            this.trigger('machineProduced', [_machine])
            if(this.activeSite?.revealAfterProduction && this.sites[this.activeSiteIndex - 1]?.id === _machine.id)
                this.revealCurrentSite()
        }

        this.emitMachineActivity(_machine, wasActive)
    }

    emitMachineActivity(_machine, _wasActive)
    {
        if(_machine.active !== _wasActive)
            this.trigger('machineActivity', [_machine])
    }

    consumeFuel(_machine)
    {
        for(const resource of ['coal', 'timber'])
        {
            if((_machine.inputs[resource] ?? 0) > 0)
            {
                _machine.inputs[resource]--
                _machine.fuelRemaining = TUNING.fuelSeconds[resource]
                this.trigger('machineChanged', [_machine])
                return true
            }
        }
        return false
    }

    advanceMilestone()
    {
        const site = this.activeSite
        if(!site)
        {
            if(!this.botOrder.revealed)
                return 'No coal-bot order is available.'
            for(const [resource, amount] of Object.entries(this.botOrder.recipe))
                this.botOrder.deposited[resource] = amount
            const bot = this.completeBotOrder()
            return `Ordered ${bot.id}.`
        }

        if(site.revealed)
        {
            for(const [resource, amount] of Object.entries(site.recipe))
                site.deposited[resource] = amount
            this.completeSite(site)
            return `Built ${site.id}.`
        }

        const previousSite = this.sites[this.activeSiteIndex - 1]
        const machine = this.machines.find((_machine) => _machine.id === previousSite?.id)
        if(!machine)
            return 'No machine is available to unlock the next milestone.'

        const definition = MACHINE_TYPES[machine.type]
        const resources = []
        const primaryInput = definition.inputs?.[0]
        if(definition.fuel)
        {
            machine.inputs.coal = TUNING.machineSlotCapacity
            machine.receivedInputs.add('coal')
            resources.push('coal')
            this.revealResourceDeposit(primaryInput)
        }
        for(const input of definition.inputs ?? [])
        {
            machine.inputs[input] = TUNING.machineSlotCapacity
            machine.receivedInputs.add(input)
            resources.push(input)
        }
        if(primaryInput && !this.activeSite?.revealAfterProduction)
            this.revealCurrentSite()
        this.trigger('machineChanged', [machine])
        return `Stocked ${machine.id} with ${resources.join(' and ')}.`
    }

    tryConstruction()
    {
        const site = this.activeSite
        if(site?.revealed && this.tryConstructionAt(site, false))
            return true
        return this.botOrder.revealed && this.tryConstructionAt(this.botOrder, true)
    }

    tryConstructionAt(_site, _botOrder)
    {
        if(Math.abs(this.player.x - _site.x) > TUNING.tileHalfSize || Math.abs(this.player.y - _site.y) > TUNING.tileHalfSize)
            return false

        const resource = Object.keys(_site.recipe).find((_resource) =>
            _site.deposited[_resource] < _site.recipe[_resource] && this.inventory[_resource] > 0
        )
        if(!resource)
            return false

        if(this.transferCooldown <= 0)
        {
            this.inventory[resource]--
            _site.deposited[resource]++
            this.transferCooldown = TUNING.transferInterval
            this.trigger(_botOrder ? 'botOrderTransfer' : 'siteTransfer', [{ site: _site, resource }])

            const complete = Object.keys(_site.recipe).every((_resource) => _site.deposited[_resource] >= _site.recipe[_resource])
            if(complete)
            {
                if(_botOrder)
                    this.completeBotOrder()
                else
                    this.completeSite(_site)
            }
        }
        return true
    }

    completeBotOrder()
    {
        const bot = this.botSystem.deploy()
        for(const resource of Object.keys(this.botOrder.deposited))
            this.botOrder.deposited[resource] = 0
        this.botOrder.recipe = getBotOrderRecipe(this.botSystem.bots.length)
        this.trigger('botOrderCompleted', [{ site: this.botOrder, bot }])
        return bot
    }

    completeSite(_site)
    {
        _site.built = true
        const definition = MACHINE_TYPES[_site.machineType]
        let machine = null
        if(definition)
        {
            const machinePosition = { x: _site.machineX ?? _site.x, y: _site.machineY ?? _site.y }
            machine = {
                id: _site.id,
                type: _site.machineType,
                x: machinePosition.x,
                y: machinePosition.y,
                inputs: {},
                output: 0,
                fuelRemaining: 0,
                progress: 0,
                active: false,
                inputBlockedUntilExit: distance(this.player, machinePosition) <= TUNING.machineRange,
                receivedInputs: new Set()
            }
            this.machines.push(machine)
        }
        else if(_site.kind === 'belt')
            this.logistics.buildSite(_site)

        if(definition?.depositId)
        {
            const deposit = this.deposits.find((_item) => _item.id === definition.depositId)
            deposit.occupied = true
            this.trigger('depositChanged', [deposit])
        }

        this.trigger('siteCompleted', [{ site: _site, machine }])
        this.activeSiteIndex++
        if(definition?.kind === 'smelter')
            this.revealResourceDeposit('coal')
        if((!definition || definition.kind !== 'smelter' || _site.id === 'siliconSmelter') && !this.activeSite?.revealAfterProduction)
            this.revealCurrentSite()
        if(_site.revealsResource)
            this.revealResourceDeposit(_site.revealsResource)
        if(_site.machineType === 'botFabricator')
        {
            this.botOrder.revealed = true
            this.trigger('botOrderRevealed', [this.botOrder])
        }
        if(!this.activeSite)
            this.trigger('factoryCompleted')
    }

    revealResourceDeposit(_resource)
    {
        const deposit = this.deposits.find((_deposit) => _deposit.resource === _resource)
        if(!deposit || deposit.revealed)
            return
        deposit.revealed = true
        this.trigger('depositRevealed', [deposit])
    }

    revealCurrentSite()
    {
        const site = this.activeSite
        if(!site || site.revealed)
            return
        site.revealed = true
        this.trigger('siteRevealed', [site])
    }

    revealBinIfNeeded()
    {
        if(this.bin.revealed || this.inventoryTotal < TUNING.backpackCapacity || this.canUseInventoryInRecipe())
            return
        this.bin.revealed = true
        this.trigger('binRevealed', [this.bin])
    }

    shouldGuideToSite(_site)
    {
        if(!_site?.revealed)
            return false

        return Object.keys(_site.recipe).some((_resource) =>
        {
            const remaining = _site.recipe[_resource] - _site.deposited[_resource]
            return remaining > 0 && this.inventory[_resource] >= remaining
        })
    }

    canUseInventoryInRecipe()
    {
        const site = this.activeSite
        if(site?.revealed && Object.keys(site.recipe).some((_resource) =>
            site.deposited[_resource] < site.recipe[_resource] && this.inventory[_resource] > 0
        ))
            return true

        if(this.botOrder.revealed && Object.keys(this.botOrder.recipe).some((_resource) =>
            this.botOrder.deposited[_resource] < this.botOrder.recipe[_resource] && this.inventory[_resource] > 0
        ))
            return true

        return this.machines.some((_machine) =>
            this.getAcceptedResources(_machine).some((_resource) => this.inventory[_resource] > 0
        ))
    }

    tryBinInput()
    {
        if(!this.bin.revealed || distance(this.player, this.bin) > TUNING.machineRange || this.canUseInventoryInRecipe())
            return false

        const resource = RESOURCE_ORDER.find((_resource) => this.inventory[_resource] > 0)
        if(!resource)
            return false

        if(this.transferCooldown <= 0)
        {
            this.inventory[resource]--
            this.bin.stored[resource]++
            this.transferCooldown = TUNING.transferInterval
            this.trigger('binInput', [{ bin: this.bin, resource }])
        }
        return true
    }

    tryMachineInput()
    {
        for(const machine of this.machines)
        {
            if(machine.inputBlockedUntilExit || distance(this.player, machine) > TUNING.machineRange)
                continue

            const resource = this.getAcceptedResources(machine).find((_resource) => this.inventory[_resource] > 0)
            if(!resource)
                continue

            if(this.transferCooldown <= 0)
            {
                this.inventory[resource]--
                machine.inputs[resource] = (machine.inputs[resource] ?? 0) + 1
                machine.receivedInputs.add(resource)
                this.transferCooldown = TUNING.transferInterval
                this.trigger('machineInput', [{ machine, resource }])
                const definition = MACHINE_TYPES[machine.type]
                const primaryInput = definition.inputs?.[0]
                if(definition.kind === 'smelter')
                {
                    if(resource === 'coal')
                        this.revealResourceDeposit(primaryInput)
                    if(resource === primaryInput && this.sites[this.activeSiteIndex - 1]?.id === machine.id)
                        this.revealCurrentSite()
                }
            }
            return true
        }
        return false
    }

    getAcceptedResources(_machine)
    {
        const definition = MACHINE_TYPES[_machine.type]
        const resources = []
        for(const resource of definition.inputs ?? [])
        {
            if((_machine.inputs[resource] ?? 0) < TUNING.machineSlotCapacity)
                resources.push(resource)
        }
        if(definition.fuel)
        {
            const fuelCount = (_machine.inputs.coal ?? 0) + (_machine.inputs.timber ?? 0)
            if(fuelCount < TUNING.machineSlotCapacity)
            {
                resources.push('coal')
                if(_machine.id !== 'ironSmelter' || _machine.receivedInputs.has('coal'))
                    resources.push('timber')
            }
        }
        return resources
    }

    tryGroundOutput()
    {
        if(this.inventoryTotal >= TUNING.backpackCapacity)
            return false
        const pile = this.logistics.groundPiles.find((_pile) => _pile.count > 0 && insideTray(this.player, _pile, TRAY_SIZES.ground))
        if(!pile)
            return false
        if(this.transferCooldown <= 0)
        {
            pile.count--
            this.inventory[pile.resource]++
            this.revealBinIfNeeded()
            this.transferCooldown = TUNING.transferInterval
            this.trigger('groundPileOutput', [{ pile, resource: pile.resource }])
        }
        return true
    }

    tryMachineOutput()
    {
        if(this.inventoryTotal >= TUNING.backpackCapacity)
            return false

        for(const machine of this.machines)
        {
            if(machine.output <= 0 || this.logistics.hasSource(machine.id))
                continue

            const outputTrayBelt = OUTPUT_TRAY_BELTS[machine.id]
            const trayOnly = outputTrayBelt && this.logistics.belts.some((_belt) => _belt.id === outputTrayBelt)
            if(trayOnly)
            {
                if(!insideTray(this.player, getMachinePort(machine, 'output'), TRAY_SIZES.port))
                    continue
            }
            else if(distance(this.player, machine) > TUNING.machineRange)
                continue

            if(this.transferCooldown <= 0)
            {
                const resource = MACHINE_TYPES[machine.type].output
                machine.output--
                this.inventory[resource]++
                this.revealBinIfNeeded()
                this.transferCooldown = TUNING.transferInterval
                this.trigger('machineOutput', [{ machine, resource }])
            }
            return true
        }
        return false
    }

    tryGathering()
    {
        if(this.inventoryTotal >= TUNING.backpackCapacity)
            return

        const deposit = this.deposits.find((_deposit) =>
            _deposit.revealed && _deposit.remaining > 0 &&
            distance(this.player, _deposit) <= TUNING.gatherRange + (_deposit.tree ? 0 : 1.15)
        )
        if(!deposit)
            return

        this.action = { type: deposit.tree ? 'chop' : 'mine', deposit }
        if(this.gatherCooldown > 0)
            return

        deposit.remaining--
        this.inventory[deposit.resource]++
        this.revealBinIfNeeded()
        this.gatherCooldown = TUNING.gatherInterval
        if(deposit.tree && deposit.remaining === 0)
            deposit.regrowAt = this.elapsed + TUNING.treeRegrowSeconds
        this.trigger('depositChanged', [deposit])
        this.trigger('gathered', [{ deposit, resource: deposit.resource }])
    }
}
